import React from "react";
import useLang from "Shared/hooks/useLanguage";
import { EditIcon } from "Shared/Icons";
import type { CassetteBlock } from "../pathologyReportData";

type Props = {
  cassettes: CassetteBlock[];
};

const BlockManagementTab = ({ cassettes }: Props) => {
  const { t } = useLang();

  return (
    <>
      <style>{`
        .pathology-block-title {
          font-weight: 700;
          font-size: 1.05rem;
          color: #212529;
          margin-bottom: 0.35rem;
        }
        .pathology-block-subtitle {
          color: #6c757d;
          font-size: 0.9rem;
          margin-bottom: 0;
          line-height: 1.5;
        }
        .pathology-block-cassette-list {
          margin-top: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .pathology-block-cassette {
          background-color: #fff;
          border: 1px solid #dee2e6;
          border-radius: 10px;
          overflow: hidden;
        }
        .pathology-block-cassette-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          padding: 4px 8px;
        }
        .pathology-block-cassette-body {
          padding: 16px 18px 18px;
          background-color: #fff;
        }
        .pathology-block-cassette-name {
          font-weight: 600;
          color: #212529;
          font-size: 0.95rem;
        }
        .pathology-block-table-wrap {
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          overflow: hidden;
        }
        .pathology-block-table {
          width: 100%;
          margin-bottom: 0;
          border-collapse: collapse;
          border-style: hidden;
        }
        .pathology-block-table thead th,
        .pathology-block-table tbody td {
          border: none !important;
          border-bottom: 1px solid #dee2e6 !important;
          box-shadow: none;
        }
        .pathology-block-table thead th {
          background-color: #fff9c4;
          color: #212529;
          font-weight: 700;
          font-size: 0.875rem;
          padding: 14px 18px;
          text-align: left;
        }
        .pathology-block-table thead th:first-child {
          text-align: center;
        }
        .pathology-block-table tbody td {
          padding: 4px 8px;
          vertical-align: middle;
          color: #212529;
          font-size: 0.9rem;
          background: #fff;
          text-align: left;
        }
        .pathology-block-table tbody tr:last-child td {
          border-bottom: none !important;
        }
        .pathology-block-table tbody td:first-child {
          text-align: center;
          width: 90px;
        }
        .pathology-block-edit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
        }
        .pathology-block-edit-btn i,
        .pathology-block-edit-btn i::before {
          color: #5cb85c !important;
          font-size: 1.1rem;
        }
        .pathology-block-edit-btn:hover i,
        .pathology-block-edit-btn:hover i::before {
          color: #4cae4c !important;
        }
        .pathology-block-stain-cell {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .pathology-block-stain-pill {
          display: inline-block;
          background-color: #e0e4f1;
          color: #212529;
          font-size: 0.8125rem;
          font-weight: 400;
          padding: 6px 14px;
          border-radius: 50rem;
          line-height: 1.25;
        }
      `}</style>

      <div>
        <div className="pathology-block-title">{t("Block management")}</div>
        <p className="pathology-block-subtitle">
          {t("Existing functionality - cassette and stain/slide tracking.")}
        </p>

        <div className="pathology-block-cassette-list">
          {cassettes.map((cass) => (
            <div key={cass.id} className="pathology-block-cassette">
              <div className="pathology-block-cassette-header">
                <span className="pathology-block-cassette-name">
                  {t(cass.title)}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-primary fw-semibold"
                >
                  {t("+ Add Stain/Slide")}
                </button>
              </div>
              <div className="pathology-block-cassette-body">
              <div className="pathology-block-table-wrap">
                <table className="pathology-block-table">
                  <thead>
                    <tr>
                      <th>{t("Action")}</th>
                      <th>{t("Slide")}</th>
                      <th>{t("Req Stain")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cass.rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <button
                            type="button"
                            className="pathology-block-edit-btn"
                            aria-label={t("Edit")}
                          >
                            {EditIcon("fa fa-edit")}
                          </button>
                        </td>
                        <td>{row.slide}</td>
                        <td className="pathology-block-stain-cell">
                          {row.stains.map((stain) => (
                            <span
                              key={stain}
                              className="pathology-block-stain-pill"
                            >
                              {stain}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default BlockManagementTab;
